import { generateUId } from '@antoree/helpers';
import { useMemo } from 'react';
import { useNotificationContext } from 'contexts/NotificationContext';
import { useChatUnread } from 'contexts/ChatUnreadContext';
import dotUnreadStyles from './dotUnread.module.scss';
import { FormattedMessage } from 'react-intl';
import { NavLink, useLocation } from 'react-router-dom';
import { useCurrentUser } from 'contexts';
import styles from './MainLayout.module.scss';
import { MenuItem } from './types';

interface MenuItemsProps {
  variant?: 'desktop' | 'mobile';
  onClick?: () => void;
}

export default function MenuItems({
  variant = 'desktop',
  onClick,
}: MenuItemsProps) {
  const { hidePayment } = useCurrentUser();
  const { hasUnreadChat } = useChatUnread();
  const location = useLocation();

  const menuItems = useMemo((): MenuItem[] => {
    const items: MenuItem[] = [
      {
        uiqueId: 'menu-claim-request',
        id: 'claim-request',
        link: '/claim-request',
        name: <FormattedMessage defaultMessage="Request " />,
      },
      {
        uiqueId: 'menu-schedule',
        id: 'schedule',
        link: '/schedule/available-time',
        name: <FormattedMessage defaultMessage="Schedule" />,
      },
      {
        uiqueId: 'menu-courses',
        id: 'courses',
        link: '/courses',
        name: <FormattedMessage defaultMessage="My courses" />,
      },
      {
        uiqueId: 'menu-payment',
        id: 'payment',
        link: '/payment',
        name: <FormattedMessage defaultMessage="Payment" />,
      },
      {
        uiqueId: 'menu-chat',
        id: 'chat',
        link: '/chat',
        name: (
          <>
            <FormattedMessage defaultMessage="Chat" />
            {hasUnreadChat && <span className={dotUnreadStyles.dotUnread} />}
          </>
        ),
      },
    ];

    if (!hidePayment) {
      items.push({
        uiqueId: 'menu-salary',
        id: 'salary--id',
        link: '/salary',
        name: <FormattedMessage defaultMessage="Salary" />,
      });
    }

    return items;
  }, [hidePayment, hasUnreadChat]);

  const containerClass =
    variant === 'mobile' ? styles.mobileMenuItems : styles.menuItemsContainer;

  return (
    <nav className={containerClass}>
      <ul>
        {menuItems.map(item => (
          <li key={item.uiqueId}>
            {item.isExternal ? (
              <a
                href={item.link}
                target="_blank"
                rel="noopener noreferrer"
                className={
                  location.pathname === item.link ? styles.activeLink : ''
                }
              >
                {item.name}
              </a>
            ) : (
              <NavLink
                activeClassName={styles.activeLink}
                to={item.link}
                onClick={onClick}
                isActive={(match, loc) => {
                  if (!match) {
                    // Custom matching for schedule subroutes
                    if (
                      item.id === 'schedule' &&
                      loc.pathname.startsWith('/schedule')
                    ) {
                      return true;
                    }
                    return false;
                  }
                  return true;
                }}
              >
                {item.name}
              </NavLink>
            )}
          </li>
        ))}
      </ul>
    </nav>
  );
}
